import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  Shield, 
  Clock, 
  Users, 
  FileText, 
  Download,
  Plus,
  Filter,
  Search,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const RiskHeatmap = () => {
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showMitigationForm, setShowMitigationForm] = useState(false);
  const [newMitigationAction, setNewMitigationAction] = useState({
    title: '',
    description: '',
    assignedTo: '',
    dueDate: ''
  });

  useEffect(() => {
    loadRiskData();
  }, []);

  const loadRiskData = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getRiskHeatmap();
      if (response.success) {
        setRiskData(response.data);
      }
    } catch (error) {
      console.error('Error loading risk data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRiskStatusUpdate = async (riskId, status) => {
    try {
      await MockDataService.updateRiskStatus(riskId, status);
      // Refresh data
      loadRiskData();
    } catch (error) {
      console.error('Error updating risk status:', error);
    }
  };

  const handleCreateMitigationAction = async () => {
    try {
      await MockDataService.createMitigationAction({
        ...newMitigationAction,
        riskId: selectedRisk?.id
      });
      setShowMitigationForm(false);
      setNewMitigationAction({ title: '', description: '', assignedTo: '', dueDate: '' });
      loadRiskData();
    } catch (error) {
      console.error('Error creating mitigation action:', error);
    }
  };

  const handleGenerateReport = async () => {
    try {
      const response = await MockDataService.generateRiskReport({
        type: 'comprehensive',
        includeTrends: true,
        includeMitigation: true
      });
      if (response.success) {
        alert('Risk report generated successfully!');
      }
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 80) return 'text-red-600';
    if (score >= 60) return 'text-orange-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'mitigated': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'monitored': return <Eye className="w-4 h-4 text-blue-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!riskData) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Failed to load risk data</p>
      </div>
    );
  }

  const filteredCategories = selectedCategory === 'all' 
    ? (riskData.riskCategories || [])
    : (riskData.riskCategories || []).filter(cat => cat.id === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Risk Heatmap</h1>
          <p className="text-gray-600">Comprehensive risk assessment and management dashboard</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleGenerateReport}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Generate Report
          </button>
        </div>
      </div>

      {/* Risk Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overall Risk Score</p>
              <p className={`text-2xl font-bold ${getRiskScoreColor(riskData.overallRiskScore)}`}>
                {riskData.overallRiskScore}
              </p>
            </div>
            <div className={`p-3 rounded-full ${getRiskLevelColor(riskData.riskLevel)}`}>
              <Shield className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Risk Level: <span className="capitalize">{riskData.riskLevel}</span>
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Critical Risks</p>
              <p className="text-2xl font-bold text-red-600">{riskData.criticalRisks}</p>
            </div>
            <div className="p-3 rounded-full bg-red-50 text-red-600">
              <AlertTriangle className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Immediate attention required</p>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">High Risks</p>
              <p className="text-2xl font-bold text-orange-600">{riskData.highRisks}</p>
            </div>
            <div className="p-3 rounded-full bg-orange-50 text-orange-600">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Priority mitigation needed</p>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Medium Risks</p>
              <p className="text-2xl font-bold text-yellow-600">{riskData.mediumRisks}</p>
            </div>
            <div className="p-3 rounded-full bg-yellow-50 text-yellow-600">
              <Activity className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Monitor and plan</p>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Low Risks</p>
              <p className="text-2xl font-bold text-green-600">{riskData.lowRisks}</p>
            </div>
            <div className="p-3 rounded-full bg-green-50 text-green-600">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Acceptable risk level</p>
        </div>
      </div>

      {/* Risk Matrix */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="text-lg font-semibold mb-4">Risk Matrix</h3>
        <div className="grid grid-cols-4 gap-2">
          <div className="text-center text-sm font-medium text-gray-600 p-2"></div>
          <div className="text-center text-sm font-medium text-gray-600 p-2">Low</div>
          <div className="text-center text-sm font-medium text-gray-600 p-2">Medium</div>
          <div className="text-center text-sm font-medium text-gray-600 p-2">High</div>
          
          <div className="text-center text-sm font-medium text-gray-600 p-2">Critical</div>
          {(riskData.riskMatrix || []).filter(r => r.impact === 'critical').map((risk, index) => (
            <div key={index} className={`text-center p-2 rounded ${risk.color} text-white font-medium`}>
              {risk.count}
            </div>
          ))}
          
          <div className="text-center text-sm font-medium text-gray-600 p-2">High</div>
          {(riskData.riskMatrix || []).filter(r => r.impact === 'high').map((risk, index) => (
            <div key={index} className={`text-center p-2 rounded ${risk.color} text-white font-medium`}>
              {risk.count}
            </div>
          ))}
          
          <div className="text-center text-sm font-medium text-gray-600 p-2">Medium</div>
          {(riskData.riskMatrix || []).filter(r => r.impact === 'medium').map((risk, index) => (
            <div key={index} className={`text-center p-2 rounded ${risk.color} text-white font-medium`}>
              {risk.count}
            </div>
          ))}
          
          <div className="text-center text-sm font-medium text-gray-600 p-2">Low</div>
          {(riskData.riskMatrix || []).filter(r => r.impact === 'low').map((risk, index) => (
            <div key={index} className={`text-center p-2 rounded ${risk.color} text-white font-medium`}>
              {risk.count}
            </div>
          ))}
        </div>
      </div>

      {/* Risk Categories */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Risk Categories</h3>
            <div className="flex space-x-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Categories</option>
                {(riskData.riskCategories || []).map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {filteredCategories.map(category => (
              <div key={category.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color }}
                    ></div>
                    <h4 className="text-lg font-medium">{category.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(category.level)}`}>
                      {category.level.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Risk Score</p>
                    <p className={`text-xl font-bold ${getRiskScoreColor(category.score)}`}>
                      {category.score}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {(category.risks || []).map(risk => (
                    <div key={risk.id} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h5 className="font-medium">{risk.title}</h5>
                            {getStatusIcon(risk.status)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(risk.impact)}`}>
                              {risk.impact.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{risk.description}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>Owner: {risk.owner}</span>
                            <span>Due: {new Date(risk.dueDate).toLocaleDateString()}</span>
                            <span>Score: <span className={getRiskScoreColor(risk.riskScore)}>{risk.riskScore}</span></span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setSelectedRisk(risk)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRiskStatusUpdate(risk.id, 'mitigated')}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risk Alerts */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Risk Alerts</h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {(riskData.riskAlerts || []).map(alert => (
              <div key={alert.id} className={`p-4 rounded-lg border-l-4 ${
                alert.type === 'critical' ? 'border-red-500 bg-red-50' :
                alert.type === 'high' ? 'border-orange-500 bg-orange-50' :
                'border-yellow-500 bg-yellow-50'
              }`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{alert.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Category: {alert.category} • Priority: {alert.priority}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    alert.status === 'open' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {alert.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mitigation Actions */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Mitigation Actions</h3>
            <button
              onClick={() => setShowMitigationForm(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Action
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {(riskData.mitigationActions || []).map(action => (
              <div key={action.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-medium">{action.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                      <span>Assigned: {action.assignedTo}</span>
                      <span>Due: {new Date(action.dueDate).toLocaleDateString()}</span>
                      <span>Progress: {action.progress}%</span>
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${action.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    action.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    action.status === 'planned' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {action.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risk Assessment History */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Assessment History</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {(riskData.riskAssessmentHistory || []).map(assessment => (
              <div key={assessment.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-medium">
                      Assessment by {assessment.assessor}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {new Date(assessment.assessmentDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Overall Score</p>
                    <p className={`text-xl font-bold ${getRiskScoreColor(assessment.overallScore)}`}>
                      {assessment.overallScore}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-sm mb-2">Changes</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {(assessment.changes || []).map((change, index) => (
                        <li key={index}>• {change.change} - {change.reason}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-sm mb-2">Recommendations</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {(assessment.recommendations || []).map((rec, index) => (
                        <li key={index}>• {rec}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mitigation Action Modal */}
      {showMitigationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Mitigation Action</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={newMitigationAction.title}
                  onChange={(e) => setNewMitigationAction({...newMitigationAction, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter action title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newMitigationAction.description}
                  onChange={(e) => setNewMitigationAction({...newMitigationAction, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                  placeholder="Enter action description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                <input
                  type="text"
                  value={newMitigationAction.assignedTo}
                  onChange={(e) => setNewMitigationAction({...newMitigationAction, assignedTo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter assignee"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={newMitigationAction.dueDate}
                  onChange={(e) => setNewMitigationAction({...newMitigationAction, dueDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowMitigationForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateMitigationAction}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Action
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskHeatmap;
